import pluralize from "pluralize";

import { sortBySortParam } from "@/utils/sort";

import {
  ALL_CATEGORIES_FILTER,
  FLAG_CATEGORY_FILTERS,
  FLAG_PRIORITY_RANK,
  FLAG_PRIORITY_VALUES,
  FlagPriority,
  FlagSortKey,
} from "../constants/flags";
import { FlagCategoryCounts, FlagCategoryFilterValue, PracticeFlag } from "../interfaces/flags";

import { compareFlagCategories } from "./flag-sort";

export function buildCategoryCounts(flags: PracticeFlag[]): FlagCategoryCounts {
  return FLAG_CATEGORY_FILTERS.reduce((counts, filter) => {
    counts[filter] =
      filter === ALL_CATEGORIES_FILTER
        ? flags.length
        : flags.filter((flag) => flag.category === filter).length;

    return counts;
  }, {} as FlagCategoryCounts);
}

/**
 * A typed search term as every flag filter compares it. Both surfaces that search flags — the
 * library page and the chart drawer — match on the name alone, case- and whitespace-insensitively,
 * so they normalize it the same way.
 */
export function normalizeFlagSearch(search: string): string {
  return search.trim().toLowerCase();
}

/**
 * Whether a flag's name matches a term from `normalizeFlagSearch`. An empty term matches every flag,
 * which is what makes a cleared search box a no-op rather than a filter that hides everything.
 */
export function matchesFlagSearch({ name }: { name: string }, normalizedSearch: string): boolean {
  return !normalizedSearch || name.toLowerCase().includes(normalizedSearch);
}

export function filterFlags(
  flags: PracticeFlag[],
  { category, search }: { category: FlagCategoryFilterValue; search: string },
): PracticeFlag[] {
  const normalizedSearch = normalizeFlagSearch(search);

  return flags.filter((flag) => {
    const matchesCategory = category === ALL_CATEGORIES_FILTER || flag.category === category;

    return matchesCategory && matchesFlagSearch(flag, normalizedSearch);
  });
}

/**
 * The warning the delete confirmation shows for one row.
 *
 * Three different decisions, so three different warnings. A flag on charts tells the user what
 * happens to those charts. A flag on none is a plain removal. A flag whose count could not be read
 * must not claim either: saying "on no charts" to someone about to delete a flag that is on twenty
 * is the one wrong answer here, so an unknown count warns about the possibility instead.
 */
export function buildDeleteFlagWarning({ name, chartCount }: PracticeFlag): string {
  if (chartCount === null) {
    return `“${name}” will be removed from your flag library. If it is on any client charts it will appear as deprecated there until a provider removes it. This can’t be undone.`;
  }

  if (chartCount === 0) {
    return `“${name}” will be removed from your flag library. This can’t be undone.`;
  }

  const charts = pluralize("client chart", chartCount, true);

  return `“${name}” is currently active on ${charts}. If deleted, it will appear as deprecated on those charts until a provider removes it. This can’t be undone.`;
}

/**
 * Category and priority are both optional on the create form, so an unset one is an ordinary row
 * rather than a data problem. It sorts after every coded value on an ascending sort — and, like
 * everything else, the other way round under the `-` prefix.
 */
const UNSET_PRIORITY_RANK = FLAG_PRIORITY_VALUES.length;

/**
 * Where a priority sorts: High, then Medium, then Low, then whatever the practice left unset.
 *
 * The library table and a chart's flag list both order by priority, and a tier means the same place
 * in both, so they rank through here rather than each choosing how an unset one compares.
 */
export function getFlagPriorityRank(priority: FlagPriority | null): number {
  return priority === null ? UNSET_PRIORITY_RANK : FLAG_PRIORITY_RANK[priority];
}

function compareNames(a: PracticeFlag, b: PracticeFlag): number {
  return a.name.localeCompare(b.name);
}

const FLAG_COMPARATORS: Record<FlagSortKey, (a: PracticeFlag, b: PracticeFlag) => number> = {
  [FlagSortKey.Name]: compareNames,
  [FlagSortKey.Category]: compareFlagCategories,
  [FlagSortKey.Priority]: (a, b) =>
    getFlagPriorityRank(a.priority) - getFlagPriorityRank(b.priority),
  // An unknown count sorts as none, so a failed count read leaves the column ordered rather than
  // scattered — every row is unknown together, so they simply tie.
  [FlagSortKey.ChartCount]: (a, b) => (a.chartCount ?? 0) - (b.chartCount ?? 0),
};

/**
 * `sort` follows the `SortableTh` convention — a bare key ascends, a `-` prefix descends. Without
 * a `sort`, flags keep their loaded order.
 */
export function sortFlags(flags: PracticeFlag[], sort: string | null): PracticeFlag[] {
  if (!sort) return flags;

  return sortBySortParam(flags, sort, FLAG_COMPARATORS, {
    fallback: compareNames,
    tiebreak: compareNames,
  });
}
